import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, Sparkles, Star } from "lucide-react";
import { lineReveal } from "../../lib/ui";

const HERO_IMG = "/himangini-hero.webp";

const HeadingLine = ({ children, i, className }) => (
  <span className="mask-line">
    <motion.span
      custom={i}
      variants={lineReveal}
      initial="hidden"
      animate="show"
      className={`block ${className}`}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const badgeRotate = useTransform(scrollYProgress, [0, 1], [0, 24]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative overflow-hidden px-4 pb-16 pt-28 sm:px-6 sm:pt-32 lg:pt-40"
    >
      <div className="mx-auto grid max-w-6xl grid-cols-1 items-end gap-10 lg:grid-cols-12">
        {/* Text */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05, duration: 0.6 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border-[3px] border-clay-ink bg-clay-lime px-4 py-1.5 font-body text-xs font-bold uppercase tracking-widest text-clay-ink shadow-brutal-sm"
          >
            <Sparkles className="h-3.5 w-3.5" strokeWidth={2.5} />
            Beauty · Lifestyle · Medicine
          </motion.div>

          <h1 className="font-display text-[15vw] font-black leading-[0.92] tracking-tighter text-clay-ink sm:text-7xl lg:text-[5.5rem]">
            <HeadingLine i={0}>HIMANGINI</HeadingLine>
            <HeadingLine i={1} className="text-clay-violet">
              SINGH
            </HeadingLine>
            <HeadingLine i={2}>RATHORE</HeadingLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.7 }}
            className="mt-6 max-w-md font-body text-base font-medium text-clay-ink/75 sm:text-lg"
          >
            A beauty creator making high-impact transition edits, shift-proof
            GRWMs & honest wear-tests — trusted by young professionals, medical
            students & junior doctors across India.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <a
              href="#contact"
              data-testid="hero-collab-btn"
              className="flex items-center gap-2 rounded-full border-[3px] border-clay-ink bg-clay-violet px-6 py-3 font-display text-base font-bold text-white shadow-clay-btn transition-transform hover:translate-x-[3px] hover:translate-y-[3px]"
            >
              Work with me
              <ArrowDownRight className="h-5 w-5" strokeWidth={2.5} />
            </a>
            <a
              href="#portfolio"
              data-testid="hero-work-btn"
              className="rounded-full border-[3px] border-clay-ink bg-white px-6 py-3 font-display text-base font-bold text-clay-ink shadow-brutal-sm transition-transform hover:translate-x-[3px] hover:translate-y-[3px]"
            >
              See the reels
            </a>
          </motion.div>
        </div>

        {/* Portrait */}
        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="relative overflow-hidden rounded-[2.5rem] border-[3px] border-clay-ink bg-clay-surface shadow-brutal-lg"
          >
            <motion.img
              src={HERO_IMG}
              alt="Himangini Singh Rathore"
              style={{ y: imgY }}
              className="h-[420px] w-full object-cover object-center sm:h-[520px]"
            />
          </motion.div>

          <motion.div
            style={{ rotate: badgeRotate }}
            className="absolute -left-4 -top-4 flex h-24 w-24 items-center justify-center rounded-full border-[3px] border-clay-ink bg-clay-pink shadow-brutal-sm sm:-left-6 sm:-top-6"
          >
            <Star className="h-8 w-8 text-white" fill="white" strokeWidth={1.5} />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1, duration: 0.6 }}
            className="absolute -bottom-5 -right-3 rounded-2xl border-[3px] border-clay-ink bg-clay-lime px-4 py-2 shadow-brutal-sm sm:-right-6"
          >
            <p className="font-display text-2xl font-black leading-none text-clay-ink">
              94.3K
            </p>
            <p className="font-body text-[10px] font-bold uppercase tracking-wider text-clay-ink/70">
              views / 30 days
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
